/**
 * Business component management for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class BusinessComponentManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listComponents(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_business_component/page', {
      params: { current: page, size, ...params },
    });
  }

  async listAllComponents(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_business_component/extend/list', { params });
  }

  async copyComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_business_component/extend/copyBusinessComponent', { data });
  }

  async createComponentByScene(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_business_component/extend/createComponentByScene', { data });
  }

  async listTemplateComps(where?: Record<string, any>): Promise<Record<string, any>[]> {
    const params = { options: JSON.stringify({ where: where ?? {} }) };
    const value = unwrap(await this.session.get('/api/cdp/cdp_business_component/extend/listTemplateComps', { params }));
    if (!Array.isArray(value)) throw new Error('Invalid template list response');
    return value;
  }

  async getTemplateInfo(uuid: string): Promise<Record<string, any>> {
    if (!/^[A-Za-z0-9_-]+$/.test(uuid)) throw new Error('Invalid component UUID');
    const value = unwrap(await this.session.get(`/api/cdp/editor/getComponent/${uuid}`));
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid component metadata');
    return value;
  }

  /** Read saved template content. Browser edits and save ownership stay with window.editor. */
  async readTemplate(uuid: string, view: '2d' | '3d' = '2d'): Promise<Record<string, any>> {
    if (view !== '2d' && view !== '3d') throw new Error('view must be 2d or 3d');
    const metadata = await this.getTemplateInfo(uuid);
    const resourcePath = metadata[view === '2d' ? 'displayUrl' : 'sceneUrl'];
    const document = await this.readStorageJson(resourcePath);
    if (!document || !Array.isArray(document.d)) throw new Error('Invalid HT document');
    return { metadata, view, resourcePath, document, source: 'saved', saved: false };
  }

  /** JSON dependencies use exact relative paths; no external URLs or traversal. */
  async readStorageJson(path: string): Promise<any> {
    if (typeof path !== 'string' || !path.endsWith('.json') || /[\\:%?#\x00-\x1f\x7f]/.test(path) || path.startsWith('/') ||
        path.split('/').some(p => !p || p === '..' || p === '.')) throw new Error('Invalid relative JSON resource path');
    return unwrap(await this.session.get(`/storage/${path.split('/').map(encodeURIComponent).join('/')}`));
  }

  async pageComponents(
    page = 1,
    size = 20,
    where?: Record<string, any>,
    order?: string[],
  ): Promise<Record<string, any>> {
    const params = {
      page,
      size,
      options: JSON.stringify({ where: where ?? {}, order: order ?? null }),
    };
    return this.session.get('/api/cdp/cdp_business_component/extend/page', { params });
  }

  async getComponent(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_business_component/get/${uuid}`);
  }

  async createComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_business_component/create', { data });
  }

  async updateComponent(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_business_component/update', { data });
  }

  async deleteComponent(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_business_component/delete/${uuid}`);
  }

  async batchDeleteComponents(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_business_component/batchDelete', { data: uuids });
  }

  async batchUploadBisComponent(
    file: File | Blob,
    params?: { uuid?: string; systemRes?: number; projectUuid?: string },
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    if (params?.uuid) formData.append('uuid', params.uuid);
    if (params?.systemRes !== undefined) formData.append('systemRes', String(params.systemRes));
    if (params?.projectUuid) formData.append('projectUuid', params.projectUuid);
    return this.session.post('/api/cdp/cdp_business_component/extend/batchUploadBisComponent', {
      data: formData,
      headers: {},
    });
  }
}

/** CDP ResponseResult uses code=1; raw resource JSON is left intact. */
function unwrap(value: any): any {
  if (value && typeof value === 'object' && 'code' in value && 'data' in value) {
    if (value.code !== 1) throw new Error('CDP API request failed');
    return value.data;
  }
  return value;
}
